import { Area } from '../area';
import { LoadingSpace } from '../loading-space';
import { Load } from '../load';
import { Box3, Object3D, Vector3 } from 'three';
import { MainView } from '../main-view';

export interface ItemOnSlope {
  isItemOnLeftSlope: boolean;
  isItemOnRightSlope: boolean;
}

export function calculateMaxLengthForCurrentLoad(
  load: Load,
  area: Area,
  loadingSpace: LoadingSpace,
): number {
  const highestPointForCurrentLoad: number = area.height - load.height;
  const isNoSlopeOnLeft: boolean = loadingSpace.airType === 2;
  const baseLength: number = isNoSlopeOnLeft
    ? loadingSpace.airLengthA - loadingSpace.airBaseB
    : (loadingSpace.airLengthA - loadingSpace.airBaseB) / 2;
  const triangleAngle: number = Math.atan(
    baseLength / (loadingSpace.airHeightC - loadingSpace.airHeightD),
  );
  return highestPointForCurrentLoad * Math.tan(triangleAngle);
}

export function calculateYOffset(
  load: Partial<Load>,
  isLeft = false,
  loadingSpace: LoadingSpace,
): number {
  const isNoSlopeOnLeft: boolean = loadingSpace.airType === 2;
  const triangleHeight: number = loadingSpace.airHeightC - loadingSpace.airHeightD;
  const triangleLength: number = isNoSlopeOnLeft
    ? loadingSpace.airLengthA - loadingSpace.airBaseB
    : (loadingSpace.airLengthA - loadingSpace.airBaseB) / 2;
  const triangleAngle: number = Math.atan(triangleHeight / triangleLength);
  let airContainerToSlope: number = isNoSlopeOnLeft
    ? loadingSpace.airBaseB
    : loadingSpace.airBaseB + (loadingSpace.airLengthA - loadingSpace.airBaseB) / 2;
  airContainerToSlope = isLeft ? 0 : airContainerToSlope;
  const currentTriangleLength: number = isLeft
    ? (loadingSpace.airLengthA - loadingSpace.airBaseB) / 2 - load.position.z
    : load.position.z + load.length - airContainerToSlope;
  const currentTriangleHeight: number = currentTriangleLength * Math.tan(triangleAngle);
  return currentTriangleHeight >= triangleHeight ? triangleHeight : currentTriangleHeight;
}

export function calculateLoadPositionOnSlope(
  load: Load,
  area: Area,
  loadingSpace: LoadingSpace,
  isLoadOnLeftSlope: boolean,
  isLoadOnRightSlope = false,
): void {
  const isNoSlopeOnLeft: boolean = loadingSpace.airType === 2;
  let offsetOnY: number = calculateYOffset(load, isLoadOnLeftSlope, loadingSpace);
  if (isLoadOnRightSlope) {
    const rightOffset: number = calculateYOffset(load, false, loadingSpace);
    offsetOnY = rightOffset >= offsetOnY ? rightOffset : offsetOnY;
  }
  const isHigherThanCeiling: boolean = load.position.y + load.height + offsetOnY > area.height;
  if (!isHigherThanCeiling) {
    load.position.setY(offsetOnY);
  } else {
    const maxLengthForLoadHeight: number = calculateMaxLengthForCurrentLoad(
      load,
      area,
      loadingSpace,
    );
    const toSlopeLength: number = isNoSlopeOnLeft
      ? loadingSpace.airBaseB
      : (loadingSpace.airLengthA - loadingSpace.airBaseB) / 2 +
        (isLoadOnLeftSlope ? 0 : loadingSpace.airBaseB);
    const maxWidthZPoint: number = isLoadOnLeftSlope
      ? toSlopeLength - maxLengthForLoadHeight
      : toSlopeLength + maxLengthForLoadHeight;
    const xPositionWithOffset: number = load.position.x;
    const zPositionWithOffsetLeft: number =
      load.position.z <= maxWidthZPoint ? maxWidthZPoint : load.position.z;
    const zPositionWithOffsetRight: number =
      load.position.z + load.length >= maxWidthZPoint
        ? maxWidthZPoint - load.length
        : load.position.z;
    const zPositionWithOffset: number = isLoadOnLeftSlope
      ? zPositionWithOffsetLeft
      : zPositionWithOffsetRight;
    load.position.set(xPositionWithOffset, area.height - load.height, zPositionWithOffset);
  }
}

export function detectCollisionsOnSlope(
  load: Load,
  loads: Load[],
  mainView: MainView,
  loadId: number,
  loadingSpace: LoadingSpace,
): boolean {
  const loadsIds: number[] = loads
    .filter(
      (load: Load) =>
        load.position.x >= 0 &&
        load.position.x <= loadingSpace.width &&
        load.position.z >= 0 &&
        load.position.z <= loadingSpace.length,
    )
    .map((load: Load) => load.id)
    .filter((loadId2: number): boolean => loadId2 !== loadId);
  const boundingBoxCollection: Box3[] = loadsIds
    .map((loadId3: number): Box3 => {
      const object: Object3D = mainView.renderingContext.scene.getObjectById(loadId3);
      return object ? new Box3().setFromObject(object) : null;
    })
    .filter((box: Box3): boolean => box !== null);
  const currentLoadBoundingBox: Box3 = new Box3().setFromObject(load);
  return boundingBoxCollection.some((boundingBox: Box3) => {
    return intersectsBoxWithThreshold(boundingBox, currentLoadBoundingBox, -50);
  });
}

export function intersectsBoxWithThreshold(box1: Box3, box2: Box3, threshold: number): boolean {
  const box1Copy: Box3 = box1.clone().expandByScalar(threshold);
  const box2Copy: Box3 = box2.clone().expandByScalar(threshold);
  return box1Copy.intersectsBox(box2Copy);
}

export function isItemOnSlope(item: Area | Load, loadingSpace: LoadingSpace): ItemOnSlope {
  const isItemLoad: boolean = item instanceof Load;
  const itemObj: Area | Load | Vector3 = isItemLoad ? (item as Load).position : item;
  const isNoSlopeOnLeft: boolean = loadingSpace.airType === 2;
  const rightSideStart: number =
    (loadingSpace.airLengthA - loadingSpace.airBaseB) / 2 + loadingSpace.airBaseB;
  const objectZPosWithLength: number = itemObj.z + item.length;
  const isItemOnRightSlope: boolean =
    itemObj.x >= 0 &&
    itemObj.x + item.width <= loadingSpace.width &&
    objectZPosWithLength >= (isNoSlopeOnLeft ? loadingSpace.airBaseB : rightSideStart) &&
    objectZPosWithLength <= loadingSpace.airLengthA;
  const isItemOnLeftSlope: boolean = isNoSlopeOnLeft
    ? false
    : itemObj.z <= (loadingSpace.length - loadingSpace.airBaseB) / 2 &&
      itemObj.z >= 0 &&
      itemObj.x >= 0 &&
      itemObj.x + item.width <= loadingSpace.width;
  return { isItemOnLeftSlope, isItemOnRightSlope };
}

export function calculateSlopeOffsets(load: Load, area: Area, loadingSpace: LoadingSpace): void {
  const isLoadOnRightSlope: boolean = isItemOnSlope(load, loadingSpace).isItemOnRightSlope;
  const isLoadOnLeftSlope: boolean = isItemOnSlope(load, loadingSpace).isItemOnLeftSlope;
  if (isLoadOnLeftSlope || isLoadOnRightSlope) {
    calculateLoadPositionOnSlope(load, area, loadingSpace, isLoadOnLeftSlope, isLoadOnRightSlope);
  }
}
